import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';

import { publicExamApi } from '../../api/examApi';
import { getBackendBaseUrl } from '../../lib/backendUrl';
import { parseApiError } from '../../lib/apiErrors';

const backendBase = getBackendBaseUrl();

export default function PublicResultPage() {
  const { resultToken } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadResult = async () => {
      try {
        const response = await publicExamApi.getResult(resultToken);
        setResult(response.data);
      } catch (error) {
        toast.error(parseApiError(error, 'Could not load result').message);
      } finally {
        setLoading(false);
      }
    };

    loadResult();
  }, [resultToken]);

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">Loading result...</div>;
  }

  if (!result) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">Result not found.</div>;
  }

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-8 text-white">
      <div className="mx-auto max-w-4xl rounded-2xl border border-slate-800 bg-slate-900/80 p-6 shadow-2xl md:p-8">
        <div className="flex flex-col gap-2 border-b border-slate-800 pb-6">
          <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Student Result</p>
          <h1 className="text-3xl font-semibold">{result.exam_title}</h1>
          <p className="text-slate-300">{result.exam_subject}</p>
          <p className="text-sm text-slate-400">
            {result.student_name} | {result.student_class} | {result.phone_number}
          </p>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-4">
          <div className="rounded-xl border border-slate-800 bg-slate-950 p-4">
            <p className="text-sm text-slate-400">Status</p>
            <p className="mt-2 text-xl font-semibold capitalize">{result.status.replace('_', ' ')}</p>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-950 p-4">
            <p className="text-sm text-slate-400">Total Marks</p>
            <p className="mt-2 text-xl font-semibold">{result.total_marks}</p>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-950 p-4">
            <p className="text-sm text-slate-400">Score</p>
            <p className="mt-2 text-xl font-semibold">{result.total_score ?? '-'} </p>
          </div>
          <div className="rounded-xl border border-slate-800 bg-slate-950 p-4">
            <p className="text-sm text-slate-400">Violations</p>
            <p className="mt-2 text-xl font-semibold">{result.violation_count}</p>
          </div>
        </div>

        {result.feedback && (
          <div className="mt-6 rounded-xl border border-slate-800 bg-slate-950 p-4">
            <p className="text-sm text-slate-400">Faculty Feedback</p>
            <p className="mt-2 text-slate-200">{result.feedback}</p>
          </div>
        )}

        {result.violation_details && Object.keys(result.violation_details).length > 0 && (
          <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-amber-100">
            {Object.entries(result.violation_details).map(([key, value]) => `${key}: ${value}`).join(' | ')}
          </div>
        )}

        {result.reviewed_file_url && (
          <a
            href={`${backendBase}${result.reviewed_file_url}`}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-xl bg-cyan-400 px-4 py-3 font-medium text-slate-950 hover:bg-cyan-300"
          >
            <ExternalLink size={18} />
            Open Corrected Paper
          </a>
        )}
      </div>
    </div>
  );
}
